/*
 *  saga to load auth user details from the just token
 */
import { uniq } from 'lodash'
import { put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import {
  createKeywordList,
  deleteKeywordList,
  getKeywordListData,
  getKeywordLists,
  updateKeywordList,
  updateKeywordLists,
} from '../../utils/api'
import { isCity } from '../../utils/locations'
import { isSignedIn } from '../../utils/userAuth'
import { selectCachedLocations } from '../../state/selectors/locationManager'
import { getLocationsInfo } from '../../state/actions/locationManager'

import {
  FETCH_KEYWORD_LISTS,
  ADD_KEYWORD_LIST,
  DELETE_KEYWORD_LIST,
  UPDATE_KEYWORD_LIST,
  GET_KEYWORD_LIST_DATA,
  fetchKeywordListsSuccess,
  fetchKeywordListsFailed,
  addKeywordListSuccess,
  deleteKeywordListSuccess,
  updateKeywordListSuccess,
  getKeywordListDataSuccess,
  getKeywordLocations,
  UPDATE_KEYWORD_LISTS,
} from './actions'
import { selectKeywordLists } from './selectors'

export default function* dashboardSagas() {
  yield takeLatest(FETCH_KEYWORD_LISTS.action, fetchKeywordListsHandler)

  yield takeEvery(ADD_KEYWORD_LIST.action, (action) =>
    addKeywordListHandler(action.name, action.onComplete),
  )

  yield takeEvery(DELETE_KEYWORD_LIST.action, (action) =>
    deleteKeywordListHandler(action.id, action.onComplete),
  )

  yield takeEvery(UPDATE_KEYWORD_LIST.action, (action) =>
    updateKeywordListHandler(action.id, action.payload, action.onComplete),
  )

  yield takeEvery(UPDATE_KEYWORD_LISTS.action, (action) =>
    updateKeywordListsHandler(action.lists, action.payload, action.onComplete),
  )

  yield takeEvery(GET_KEYWORD_LIST_DATA.action, (action) =>
    getListDataHandler(action.id, action.onComplete),
  )
}

function* fetchKeywordListsHandler() {
  try {
    if (!isSignedIn()) {
      yield put(
        fetchKeywordListsSuccess({
          lists: [],
        }),
      )
    } else {
      const listsResponse = yield getKeywordLists()

      yield put(
        fetchKeywordListsSuccess({
          lists: listsResponse.data,
        }),
      )
    }
  } catch (error) {
    console.error(error)
    fetchKeywordListsFailed(error)
  }
}

function* addKeywordListHandler(name, onComplete) {
  try {
    const listResponse = yield createKeywordList(name)
    const keywordLists = yield select(selectKeywordLists())

    yield put(
      addKeywordListSuccess({
        lists: [listResponse.data, ...keywordLists],
      }),
    )
    if (onComplete) {
      onComplete({ data: listResponse.data })
    }
  } catch (error) {
    console.error(error)
    if (onComplete) {
      onComplete({ error })
    }
  }
}

function* deleteKeywordListHandler(id, onComplete) {
  try {
    yield deleteKeywordList(id)
    const keywordLists = yield select(selectKeywordLists())

    yield put(
      deleteKeywordListSuccess({
        lists: keywordLists.filter((l) => l.id !== id),
      }),
    )
    if (onComplete) {
      onComplete({ data: true })
    }
  } catch (error) {
    console.error(error)
    if (onComplete) {
      onComplete({ error })
    }
  }
}

function* updateKeywordListHandler(id, { name, append, remove }, onComplete) {
  try {
    const response = yield updateKeywordList(id, name, append, remove)
    const keywordLists = yield select(selectKeywordLists())

    // if redux has keywordLists, update it
    if (keywordLists) {
      keywordLists.splice(
        keywordLists.findIndex((k) => k.id === id),
        1,
        response.data,
      )
    }

    yield put(
      updateKeywordListSuccess({
        lists: keywordLists,
        currentList: response.data,
      }),
    )

    if (onComplete) {
      onComplete({ data: response.data })
    }
  } catch (error) {
    console.error(error)
    if (onComplete) {
      onComplete({ error })
    }
  }
}

function* updateKeywordListsHandler(lists, { append }, onComplete) {
  try {
    const listIds = lists.map((list) => list.id)
    const { data } = yield updateKeywordLists(listIds, append)
    const keywordLists = yield select(selectKeywordLists())

    if (keywordLists) {
      Object.keys(data).forEach((id) => {
        if (!data[id].error) {
          keywordLists.splice(
            keywordLists.findIndex((keywordList) => keywordList?.id === id),
            1,
            data[id].keyword_list,
          )
        }
      })
    }

    yield put(
      updateKeywordListSuccess({
        lists: keywordLists,
      }),
    )

    const updatedIds = Object.keys(data)

    const keywordsAddedCount = updatedIds.reduce(
      (prev, current) => prev + data[current].keywords_added || 0,
      0,
    )

    const notificationList = updatedIds.map((id) => {
      const item = data[id]

      if (item.error) {
        const errorList = lists.find((list) => list.id === id)

        return {
          id,
          success: false,
          name: errorList.name,
        }
      }

      return {
        name: item.keyword_list.name,
        id,
        keywordsAddedCount: item.keywords_added,
        duplicatedKWs: append.length - item.keywords_added,
        success: true,
      }
    })

    if (onComplete) {
      onComplete({
        data: {
          notificationList,
          keywordsAddedCount,
        },
      })
    }
  } catch (error) {
    console.error(error)
    if (onComplete) {
      onComplete({ error })
    }
  }
}

function* getListDataHandler(id, onComplete) {
  try {
    const response = yield getKeywordListData(id)
    if (
      response.data &&
      response.data.keywords &&
      response.data.keywords.length > 0
    ) {
      const cachedLocations = yield select(selectCachedLocations())
      const locIds = uniq(
        response.data.keywords
          .filter((k) => isCity(k.loc_id) && !cachedLocations[k.loc_id])
          .map((k) => k.loc_id),
      )

      if (locIds.length > 0) {
        yield put(getKeywordLocations())
        yield put(getLocationsInfo(locIds, null, true))
      }
    }
    yield put(getKeywordListDataSuccess(response.data))
    if (onComplete) {
      onComplete({ data: true })
    }
  } catch (error) {
    console.error(error)
    if (onComplete) {
      onComplete({ error })
    }
  }
}
